import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Star, MapPin, Users, Settings as SettingsIcon, Sparkles, Tag } from "lucide-react";
import { formatMoney, partnerCurrency, partnerTripDays, partnerTripTotal } from "../lib/partnerPricing";
import ListingImage from "./ListingImage";
import { FRACTIONAL_ENABLED } from "../lib/featureFlags";

/**
 * VehicleCard — v2 Figma redesign.
 * - Image-forward (60% of card height, large rounded-t corners)
 * - Floating "Fractional" pill (top-left, blue) and rating pill (top-right, white)
 * - Pricing block: optional crossed-out original (`v.original_rate`) + bold current
 *   plus a "Save X%" tag that comes from server-side promotional pricing OR
 *   client-side smart-calendar tier (if the user picked a low-tier date upstream)
 * - Dual CTAs: gradient "Rent" + outlined "Invest" (greyed when not investable)
 */
export default function VehicleCard({ v, index = 0, startDate, endDate }) {
  const navigate = useNavigate();

  // External (partner) cars route to a read-only mirror page; hosted cars go
  // through the normal vehicle detail / booking flow.
  const isExternal = !!v.external;

  const qs = new URLSearchParams();
  if (startDate) qs.set("start", startDate);
  if (endDate) qs.set("end", endDate);
  const tail = qs.toString();
  const detailHref = isExternal
    ? `/external/${v.vehicle_id}${tail ? `?${tail}` : ""}`
    : `/vehicles/${v.vehicle_id}${tail ? `?${tail}` : ""}`;

  const goRent = (e) => {
    e.preventDefault(); e.stopPropagation();
    // Pass the full external payload via state so the mirror page can render
    // immediately without an extra round trip (the BookCars demo doesn't have
    // a single-car-by-id public endpoint we can hit cheaply).
    navigate(detailHref, isExternal ? { state: { car: v } } : undefined);
  };
  const goInvest = (e) => { e.preventDefault(); e.stopPropagation(); if (v.has_offering && v.offering_id) navigate(`/invest/${v.offering_id}`); };

  const investable = !!v.has_offering;
  const soldOut = v.offering_status === "sold_out";

  // Promotional discount: if backend ships `original_rate` we render crossed-out.
  const orig = v.original_rate && v.original_rate > v.daily_rate ? v.original_rate : null;
  const savePct = orig ? Math.round(((orig - v.daily_rate) / orig) * 100) : null;
  const tripTotal = isExternal ? partnerTripTotal(v) : null;
  const tripDays = isExternal ? partnerTripDays(v) : null;
  const cur = partnerCurrency(v);

  return (
    <Link
      to={detailHref}
      state={isExternal ? { car: v } : undefined}
      className="zc-card overflow-hidden block fade-up group"
      style={{ animationDelay: `${0.05 * (index % 6)}s` }}
      data-testid={`vehicle-card-${v.vehicle_id}`}
    >
      {/* IMAGE BLOCK */}
      <div className="relative aspect-[4/3] overflow-hidden bg-[var(--zc-bg-3)]">
        <ListingImage images={v.images} alt={`${v.make} ${v.model}`} className="w-full h-full object-cover tilt-img transition-transform duration-700 group-hover:scale-[1.04]" />
        {/* Top-left chips */}
        <div className="absolute top-3 left-3 flex flex-col gap-1.5 items-start">
          <span className="zc-badge bg-white/95 text-[var(--zc-text)]">
            {v.vehicle_type}
          </span>
          {FRACTIONAL_ENABLED && investable && (
            <span className="zc-badge flex items-center gap-1 text-white shadow-sm" style={{ background: "var(--zc-secondary)" }} data-testid={`fractional-flag-${v.vehicle_id}`}>
              <Sparkles size={9} /> {soldOut ? "Sold out" : "Fractional"}
            </span>
          )}
          {isExternal && (
            <span className="zc-badge flex items-center gap-1 text-white shadow-sm" style={{ background: "rgba(15,15,18,0.85)" }} data-testid={`partner-flag-${v.vehicle_id}`}>
              Partner · {v.external_source || "Live"}
            </span>
          )}
        </div>
        {/* Top-right rating */}
        {(v.rating_count > 0 || (v.avg_rating && v.avg_rating > 0)) && (
          <div className="absolute top-3 right-3 flex items-center gap-1 text-xs font-bold bg-white/95 px-2.5 py-1 rounded-full">
            <Star size={11} className="text-[var(--zc-accent)]" fill="currentColor" />
            {v.avg_rating?.toFixed?.(1)}
          </div>
        )}
        {/* Save tag (if discounted) */}
        {savePct && (
          <div className="absolute bottom-3 left-3 zc-gradient-bg text-white zc-badge flex items-center gap-1 shadow-md" data-testid={`save-${v.vehicle_id}`}>
            <Tag size={10} /> Save {savePct}%
          </div>
        )}
      </div>

      {/* DETAILS BLOCK */}
      <div className="p-5 flex flex-col gap-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="zc-title text-xl leading-tight truncate">{v.make} {v.model}</div>
            <div className="text-xs text-[var(--zc-text-3)] mt-1">{v.year} · {v.transmission}</div>
          </div>
          <div className="text-right shrink-0">
            {orig && <div className="zc-price-strike text-xs">${orig}</div>}
            <div className="zc-price text-2xl leading-none zc-price-now">
              {formatMoney(v.daily_rate, cur)}
            </div>
            <div className="zc-unit mt-0.5">/day</div>
            {tripTotal > 0 && tripDays > 0 && (
              <div className="zc-meta text-[var(--zc-text-2)] mt-1" data-testid={`trip-total-${v.vehicle_id}`}>
                {formatMoney(tripTotal, cur)} · {tripDays}d
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3 text-xs text-[var(--zc-text-2)] pt-2 border-t border-[var(--zc-border)]">
          <span className="flex items-center gap-1"><MapPin size={11} /> {v.location_city}</span>
          <span className="flex items-center gap-1"><Users size={11} /> {v.seats}</span>
          <span className="flex items-center gap-1"><SettingsIcon size={11} /> {v.transmission?.slice(0, 4)}</span>
        </div>
        <div className={"grid gap-2 pt-1 " + (FRACTIONAL_ENABLED ? "grid-cols-2" : "grid-cols-1")}>
          {FRACTIONAL_ENABLED && (
            investable ? (
              <button
                onClick={goInvest}
                disabled={soldOut}
                className="h-10 zc-btn-outline-secondary text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid={`invest-${v.vehicle_id}`}
                title={soldOut ? "Offering sold out" : "Buy fractions in this vehicle"}
              >
                {soldOut ? "Sold out" : "Invest"}
              </button>
            ) : (
              <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                disabled
                aria-disabled="true"
                className="h-10 text-xs rounded-full border cursor-not-allowed"
                style={{ background: "var(--zc-bg-3)", borderColor: "var(--zc-border)", color: "var(--zc-text-3)" }}
                data-testid={`invest-disabled-${v.vehicle_id}`}
                title="Investment not yet available for this vehicle"
              >
                Invest
              </button>
            )
          )}
          <button onClick={goRent} className="h-10 zc-btn-primary text-xs" data-testid={`rent-${v.vehicle_id}`}>
            {isExternal ? "View" : "Rent"}
          </button>
        </div>
      </div>
    </Link>
  );
}
